import { useCallback, useState, useContext } from 'react';

import CameraOutlined from '@ant-design/icons/CameraOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import { Typography } from '@material-ui/core';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LoadingButton from '@mui/lab/LoadingButton';
import { Grid, IconButton, TextField, Tooltip } from '@mui/material';
import { Button } from 'antd';
import { CharCount } from 'components/Presentational';
import ImageSelector, { IImageSelectorResult } from 'components/Shared/ImageSelector';
import Toast from 'components/Shared/Toast';
import { isEmpty } from 'helpers/lodash';
import { IEventForm } from 'interfaces/event';
import { useFormContext, Controller } from 'react-hook-form';

import FormContext from '../../../Context';
import * as S from './styles';

const FormOne = () => {
  const [imageSelectorOpen, setImageSelectorOpen] = useState(false);

  const { handleEdit, isCreate, loadingEdit, jobsLotPending, setCurrentStep, setStepsEnables, stepsEnables } =
    useContext(FormContext);

  const {
    register,
    formState: { errors },
    watch,
    setError,
    handleSubmit,
    setValue,
    clearErrors,
    control
  } = useFormContext<IEventForm>();

  const imageBase64Watch = watch('image_base64');
  const imagePathWatch = watch('image_path');

  const handleNext = useCallback(() => {
    const enablesSteps = Array.from(stepsEnables);
    if (!enablesSteps.includes('2')) {
      enablesSteps.push('2');
      setStepsEnables(enablesSteps);
    }

    setCurrentStep('2');
  }, [stepsEnables, setCurrentStep, setStepsEnables]);

  const handleSelectImage = (image: IImageSelectorResult) => {
    if (!image) {
      setImageSelectorOpen(false);

      return;
    }

    setValue('image_base64', image.base64);
    setValue('image_path', undefined);
    setValue('image_name', image.filename);
    setValue('image_id', null);

    setImageSelectorOpen(false);
    clearErrors('image_base64');
  };

  const handleDeleteImage = () => {
    setValue('image_base64', null);
    setValue('image_name', null);
    setValue('image_path', undefined);
    setValue('image_id', null);
    setError('image_base64', { message: 'Selecione uma imagem' });
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={12}>
        <Controller
          control={control}
          name='title'
          render={({ field: { onChange, value } }) => (
            <TextField
              fullWidth
              label='Título'
              variant='outlined'
              inputProps={{ maxLength: 100 }}
              helperText={errors.title?.message}
              error={!!errors.title}
              onChange={event => {
                onChange(event.target.value);
              }}
              value={value}
              id='title-form-one'
            />
          )}
        />
        <CharCount max={100} value={watch('title')} align='right' />
      </Grid>
      <Grid item xs={12} md={8}>
        <Grid container spacing={8}>
          <Grid item xs={12} md={12}>
            <Controller
              control={control}
              name='helpsupport_email'
              render={({ field: { onChange, value } }) => (
                <TextField
                  fullWidth
                  className='help-support'
                  InputLabelProps={{
                    shrink: true,
                    style: { pointerEvents: 'auto' }
                  }}
                  label={
                    <S.TooltipWrapper>
                      E-mail de Suporte
                      <Tooltip placement='top' title='As dúvidas de seus clientes serão direcionadas para este e-mail'>
                        <IconButton>
                          <HelpOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </S.TooltipWrapper>
                  }
                  variant='outlined'
                  helperText={errors.helpsupport_email?.message}
                  error={!!errors.helpsupport_email}
                  onChange={event => {
                    onChange(event.target.value.trim());
                  }}
                  value={value}
                  id='help_support-form-one'
                />
              )}
            />
          </Grid>
        </Grid>
      </Grid>

      {!imageBase64Watch && !imagePathWatch && (
        <Grid
          alignSelf='center'
          container
          item
          justifyContent='center'
          xs={12}
          md={4}
          sx={{ cursor: 'pointer' }}
          onClick={() => setImageSelectorOpen(true)}
        >
          <Grid container item justifyContent='center' xs={12}>
            <CameraOutlined style={{ fontSize: '42px' }} />
          </Grid>
          <Grid container item justifyContent='center' xs={12}>
            <Typography>Adicionar Imagem</Typography>
          </Grid>
          <Grid container item justifyContent='center' xs={12}>
            <S.CustomizedTypography>200x200 pixels</S.CustomizedTypography>
          </Grid>
          {errors?.image_base64 && (
            <Grid container item justifyContent='center' xs={12} sx={{ padding: '10px 0' }}>
              <S.ErrorMessage>{errors?.image_base64?.message}</S.ErrorMessage>
            </Grid>
          )}
        </Grid>
      )}

      {(imageBase64Watch || imagePathWatch) && (
        <Grid alignSelf='center' container item justifyContent='center' xs={12} md={4}>
          <div className='image-container'>
            <S.Wrapper onClick={handleDeleteImage}>
              <img src={imageBase64Watch || imagePathWatch} alt='Preview de foto do evento'></img>
              <S.CustomizedDeleteIcon />
            </S.Wrapper>
            <S.CustomizedTypography>Imagem de capa</S.CustomizedTypography>
          </div>
        </Grid>
      )}

      {imageSelectorOpen && (
        <ImageSelector
          opened={imageSelectorOpen}
          height={200}
          width={200}
          onComplete={image => handleSelectImage(image)}
        />
      )}

      <Grid item xs={12} md={12}>
        <TextField
          fullWidth
          label='Fale um pouco sobre o evento'
          variant='outlined'
          helperText={errors.description?.message}
          error={!!errors.description}
          multiline
          id='description-form-one'
          {...register('description')}
        />
      </Grid>
      <Grid container item justifyContent='flex-end' xs={12}>
        {isCreate && (
          <Button
            onClick={() => {
              if (!isEmpty(errors)) {
                Toast.error('Verifique todos os campos');
                return;
              }
              handleSubmit(handleNext)();
            }}
            type='primary'
            size='large'
            id='step-forward-btn-form-1'
          >
            Próximo
          </Button>
        )}

        {!isCreate && !loadingEdit && !jobsLotPending && (
          <Button
            onClick={() => handleSubmit(handleEdit)()}
            type='primary'
            size='large'
            id='save-btn-form-5'
            icon={<SaveOutlined />}
          >
            Salvar
          </Button>
        )}

        {((!isCreate && loadingEdit) || !!jobsLotPending) && (
          <LoadingButton loading loadingIndicator='Salvando' startIcon={<SaveOutlined />} variant='outlined'>
            Salvando
          </LoadingButton>
        )}
      </Grid>

      {!!jobsLotPending && (
        <Grid container item justifyContent='flex-end' xs={12}>
          <div className='bottom-legend-button'>{jobsLotPending}</div>
        </Grid>
      )}
      <style>
        {`
          #sale_url-form-one {
            padding-left: 10px;
          }
        `}
      </style>
    </Grid>
  );
};
export default FormOne;
